'use client';

import React from 'react';
import { SectionWrapper } from './styleConstants';
import { Container, CustomImage } from 'components/shared';
import { ButtonV2, type ButtonV2Props } from '../shared';
import { BackgroundVideo } from './BackgroundVideo';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import styles from './Banner.module.css';

type PositionOption =
  | 'top'
  | 'top-left'
  | 'top-right'
  | 'center'
  | 'left'
  | 'right'
  | 'bottom'
  | 'bottom-left'
  | 'bottom-right';

type ResponsiveSource = string | { mobile: string; web: string };

type ImageBackground = {
  type: 'image';
  /** Accepts a single src string or responsive `{ mobile, web }` */
  src: ResponsiveSource;
  alt?: string;
};

type VideoBackground = {
  type: 'video';
  /** Accepts a single src string or responsive `{ mobile, web }` */
  src: ResponsiveSource;
  /** Accepts a single poster string or responsive `{ mobile, web }` */
  poster?: ResponsiveSource;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  playsInline?: boolean;
  /** When true, video will play only once when first visible and not replay on re-entry */
  playOnce?: boolean;
  /** Visibility threshold at which to play (pause below). Defaults to 0.1 */
  visibilityThreshold?: number;
  /** When true, shows a replay button overlay */
  showReplayButton?: boolean;
};

type ColorBackground = {
  type: 'color';
  color: string;
};

type Background = ImageBackground | VideoBackground | ColorBackground;

/** Optional foreground image with responsive src; when set, it sits between text and bottom and flexes to fill space */
export type ForegroundImageConfig = {
  src: ResponsiveSource;
  alt?: string;
};

export interface BannerProps
  extends Omit<React.ComponentPropsWithoutRef<'section'>, 'title'> {
  className?: string;
  id?: string;
  caption?: React.ReactNode;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Pass props for ButtonV2. Include `children` for the label */
  cta?: ButtonV2Props;
  /** Pass props for secondary ButtonV2. Include `children` for the label */
  ctaAlt?: ButtonV2Props;
  /** Controls vertical and horizontal alignment. e.g. 'top', 'top-right'. Defaults to 'center' */
  position?: PositionOption;
  /** Background media - image or video */
  background?: Background;
  /** Optional foreground image (responsive mobile/web); sits between text and bottom, flexes to available height */
  foregroundImage?: ForegroundImageConfig;
  /** When true, on mobile the foreground image goes edge-to-edge (100vw) and uses object-fit: cover to fill space */
  foregroundImageFullBleedOnMobile?: boolean;
  /** Optional inline styles for the background wrapper */
  backgroundStyles?: React.CSSProperties;
  /** Optional inline styles for the content wrapper (e.g., paddingTop/paddingBottom) */
  contentContainerStyles?: React.CSSProperties;
  /** Optional inline styles for the section wrapper */
  sectionStyles?: React.CSSProperties;
  /** Enable grid-specific desktop typography when used inside BannerGrid */
  inGrid?: boolean;
  /** Override background eager-loading; defaults to `!inGrid` when unset */
  eagerBackground?: boolean;
  /** CSS color value for content text */
  contentColor?: string;
  /** Select content layout style, defaults to 'default' */
  contentStyle?: 'default' | 'blurred-card';
  /** When true, adds vertical space via wrapper padding to avoid margin collapse */
  hasVerticalMargin?: boolean;
  /** Optional note rendered below the CTA buttons (e.g. availability message) */
  note?: React.ReactNode;
  /** Optional custom content component to override the default rendering */
  CustomContentComponent?: React.ComponentType<BannerContentProps>;
  /** Optional custom background component to override the default background rendering */
  CustomBackgroundComponent?: React.ComponentType<BannerBackgroundProps>;
}

export interface BannerContentProps {
  caption?: React.ReactNode;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  cta?: ButtonV2Props;
  ctaAlt?: ButtonV2Props;
  position: PositionOption;
  inGrid?: boolean;
  horizontalAlign: 'left' | 'center' | 'right';
  contentColor: string;
  contentContainerStyles?: React.CSSProperties;
  note?: React.ReactNode;
}

export interface BannerBackgroundProps {
  background?: Background;
  selectSrc: (input?: ResponsiveSource) => string | undefined;
  resolveVideoSrc: (input?: string) => string | undefined;
  resolveImageSrc: (input?: string) => string | undefined;
  backgroundStyles?: React.CSSProperties;
}

const captionColorStyle = (color?: string) =>
  ({ ['--caption-color']: color } as React.CSSProperties);
const titleColorStyle = (color?: string) =>
  ({ ['--title-color']: color } as React.CSSProperties);
const subtitleColorStyle = (color?: string) =>
  ({ ['--subtitle-color']: color } as React.CSSProperties);
const noteColorStyle = (color?: string) =>
  ({ ['--note-color']: color } as React.CSSProperties);

const BlurredCardContent: React.FC<BannerContentProps> = ({
  caption,
  title,
  subtitle,
  cta,
  ctaAlt,
  horizontalAlign,
  contentColor,
  note,
}) => {
  return (
    <div className={styles.blurredCard}>
      {(caption || title || subtitle) && (
        <div className={styles.textStack} data-align={horizontalAlign}>
          {!!caption && (
            <div className={styles.caption} style={captionColorStyle(contentColor)}>
              {caption}
            </div>
          )}
          {!!title && (
            <h2 className={styles.title} style={titleColorStyle(contentColor)}>
              {title}
            </h2>
          )}
          {!!subtitle && (
            <p className={styles.subtitle} style={subtitleColorStyle(contentColor)}>
              {subtitle}
            </p>
          )}
        </div>
      )}

      {(cta || ctaAlt) && (
        <div className={styles.buttons}>
          {!!cta && <ButtonV2 {...cta}>{cta.children}</ButtonV2>}
          {!!ctaAlt && <ButtonV2 {...ctaAlt}>{ctaAlt.children}</ButtonV2>}
        </div>
      )}
      {!!note && (
        <p className={styles.note} style={noteColorStyle(contentColor)}>
          {note}
        </p>
      )}
    </div>
  );
};

const DefaultContent: React.FC<BannerContentProps> = ({
  caption,
  title,
  subtitle,
  cta,
  ctaAlt,
  horizontalAlign,
  contentColor,
  note,
}) => {
  return (
    <>
      {(caption || title || subtitle) && (
        <div className={styles.textStack} data-align={horizontalAlign}>
          {!!caption && (
            <div className={styles.caption} style={captionColorStyle(contentColor)}>
              {caption}
            </div>
          )}
          {!!title && (
            <h2 className={styles.title} style={titleColorStyle(contentColor)}>
              {title}
            </h2>
          )}
          {!!subtitle && (
            <p className={styles.subtitle} style={subtitleColorStyle(contentColor)}>
              {subtitle}
            </p>
          )}
        </div>
      )}

      {(cta || ctaAlt) && (
        <div className={styles.buttons}>
          {!!cta && <ButtonV2 {...cta}>{cta.children}</ButtonV2>}
          {!!ctaAlt && <ButtonV2 {...ctaAlt}>{ctaAlt.children}</ButtonV2>}
        </div>
      )}
      {!!note && (
        <p className={styles.note} style={noteColorStyle(contentColor)}>
          {note}
        </p>
      )}
    </>
  );
};

const Banner: React.FC<BannerProps> = ({
  className,
  id,
  caption,
  title,
  subtitle,
  cta,
  ctaAlt,
  position = 'center',
  background,
  foregroundImage,
  foregroundImageFullBleedOnMobile,
  backgroundStyles,
  contentContainerStyles,
  inGrid,
  eagerBackground,
  contentColor = '#fff',
  sectionStyles,
  // The below prop is for future use
  contentStyle = 'default',
  hasVerticalMargin,
  note,
  CustomContentComponent,
  CustomBackgroundComponent,
  ...restProps
}) => {
  const { width } = useWindowSize();
  const horizontalAlign: 'left' | 'center' | 'right' = position.includes('left')
    ? 'left'
    : position.includes('right')
    ? 'right'
    : 'center';

  const selectSrc = (input?: ResponsiveSource): string | undefined => {
    if (!input) return undefined;
    if (typeof input === 'string') return input;
    if (!width) return input.web;
    return width < 768 ? input.mobile : input.web;
  };

  const resolveVideoSrc = (input?: string) => {
    if (!input) return undefined;
    const isAbsolute = /^(https?:)?\/\//.test(input);
    if (isAbsolute) return input;
    const normalized = input.startsWith('/') ? input : `/${input}`;
    return getCompressedAssetUrl(normalized);
  };

  const resolveImageSrc = (input?: string) => {
    if (!input) return undefined;
    const isAbsolute = /^(https?:)?\/\//.test(input);
    if (isAbsolute) return input;
    const normalized = input.startsWith('/') ? input : `/${input}`;
    return getCompressedAssetUrl(normalized);
  };

  // Derive an accessible name if none is provided
  const ariaLabelFromProps = (
    restProps as unknown as { [key: string]: string | undefined }
  )['aria-label'];
  const derivedLabel =
    typeof title === 'string'
      ? `${title} banner`
      : typeof caption === 'string'
      ? `${caption} banner`
      : undefined;
  const computedAriaLabel = ariaLabelFromProps ?? derivedLabel;

  const isTop = position.includes('top');
  const isBottom = position.includes('bottom');
  const sectionContentStyle = {
    ...sectionStyles,
    ['--sc-align-items']: foregroundImage
      ? 'stretch'
      : isTop
      ? 'start'
      : isBottom
      ? 'end'
      : 'center',
    ['--sc-pad-top']: !inGrid && isTop ? '80px' : '0',
    ['--sc-pad-bottom']: !inGrid && isBottom ? '96px' : '0',
    ['--sc-pad-top-md']: !inGrid && isTop ? '90px' : '0',
    ['--sc-pad-bottom-md']: !inGrid && isBottom ? '120px' : '0',
  } as React.CSSProperties;

  const contentHorizontal = position.includes('left')
    ? 'flex-start'
    : position.includes('right')
    ? 'flex-end'
    : 'center';
  const contentStyleVars = {
    ...contentContainerStyles,
    ['--content-align-items']: contentHorizontal,
    ['--content-text-align']:
      contentHorizontal === 'flex-start'
        ? 'left'
        : contentHorizontal === 'flex-end'
        ? 'right'
        : 'center',
    ['--content-pad-top']: inGrid ? '64px' : isTop ? '0' : '96px',
    ['--content-pad-bottom']: inGrid ? '64px' : isBottom ? '0' : '96px',
    ['--content-pad-top-md']: inGrid ? '64px' : isTop ? '0' : '120px',
    ['--content-pad-bottom-md']: inGrid ? '64px' : isBottom ? '0' : '120px',
  } as React.CSSProperties;

  return (
    <SectionWrapper
      as={inGrid ? 'div' : 'section'}
      id={id}
      $hasVerticalMargin={hasVerticalMargin}
      aria-label={computedAriaLabel}
      role={
        (restProps as unknown as { [key: string]: string | undefined }).role ??
        (inGrid && computedAriaLabel ? 'region' : undefined)
      } // WEB ACCESSIBILITY MAINTENANCE : Added role='region' only when rendered as 'div' and accessible name present
      {...restProps}
    >
      <div
        className={[styles.sectionContent, className].filter(Boolean).join(' ')}
        data-has-fg={foregroundImage ? 'true' : undefined}
        data-fg-fullbleed={foregroundImageFullBleedOnMobile ? 'true' : undefined}
        style={sectionContentStyle}
      >
        {(background || CustomBackgroundComponent) && (
          <div className={styles.backgroundLayer} aria-hidden>
            {CustomBackgroundComponent ? (
              <CustomBackgroundComponent
                background={background}
                selectSrc={selectSrc}
                resolveVideoSrc={resolveVideoSrc}
                resolveImageSrc={resolveImageSrc}
                backgroundStyles={backgroundStyles}
              />
            ) : background?.type === 'image' ? (
              <CustomImage
                src={selectSrc(background.src) as string}
                alt={background.alt ?? ''}
                fill
                priority={eagerBackground ?? !inGrid}
                sizes="100vw"
                style={{ objectFit: 'cover', ...backgroundStyles }}
              />
            ) : background?.type === 'video' ? (
              <BackgroundVideo
                src={resolveVideoSrc(selectSrc(background.src))}
                poster={resolveImageSrc(selectSrc(background.poster))}
                autoPlay={background.autoPlay ?? true}
                loop={background.loop ?? true}
                muted={background.muted ?? true}
                playsInline={background.playsInline ?? true}
                playOnce={background.playOnce ?? false}
                visibilityThreshold={background.visibilityThreshold ?? 0.1}
                showReplayButton={background.showReplayButton ?? false}
                style={backgroundStyles}
              />
            ) : background?.type === 'color' ? (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: background.color,
                  ...backgroundStyles,
                }}
                aria-hidden
              />
            ) : null}
          </div>
        )}

        {foregroundImage ? (
          <div className={styles.contentColumn}>
            <Container>
              <div className={styles.content} style={contentStyleVars}>
                {CustomContentComponent ? (
                  <CustomContentComponent
                    caption={caption}
                    title={title}
                    subtitle={subtitle}
                    cta={cta}
                    ctaAlt={ctaAlt}
                    position={position}
                    inGrid={inGrid}
                horizontalAlign={horizontalAlign}
                contentColor={contentColor}
                contentContainerStyles={contentContainerStyles}
                    note={note}
                  />
                ) : (
                  (() => {
                    const contentProps = {
                      caption,
                      title,
                      subtitle,
                      cta,
                      ctaAlt,
                      position,
                      inGrid,
                      horizontalAlign,
                      contentColor,
                      contentContainerStyles,
                      note,
                    };
                    switch (contentStyle) {
                      case 'default':
                        return <DefaultContent {...contentProps} />;
                      case 'blurred-card':
                        return <BlurredCardContent {...contentProps} />;
                      default:
                        return <DefaultContent {...contentProps} />;
                    }
                  })()
                )}
              </div>
            </Container>
            <div
              className={styles.foregroundImageWrapper}
              aria-hidden
              data-fullbleed={foregroundImageFullBleedOnMobile ? 'true' : undefined}
            >
              <CustomImage
                src={selectSrc(foregroundImage.src) as string}
                alt={foregroundImage.alt ?? ''}
                fill
                priority={!inGrid}
                sizes="100vw"
                style={{
                  objectFit: 'contain',
                  objectPosition: 'bottom center',
                }}
              />
            </div>
          </div>
        ) : (
          <Container>
            <div className={styles.content} style={contentStyleVars}>
              {CustomContentComponent ? (
                <CustomContentComponent
                  caption={caption}
                  title={title}
                  subtitle={subtitle}
                  cta={cta}
                  ctaAlt={ctaAlt}
                  position={position}
                  inGrid={inGrid}
                  horizontalAlign={horizontalAlign}
                  contentColor={contentColor}
                  contentContainerStyles={contentContainerStyles}
                  note={note}
                />
              ) : (
                (() => {
                  const contentProps = {
                    caption,
                    title,
                    subtitle,
                    cta,
                    ctaAlt,
                    position,
                    inGrid,
                    horizontalAlign,
                    contentColor,
                    contentContainerStyles,
                    note,
                  };

                  switch (contentStyle) {
                    case 'default':
                      return <DefaultContent {...contentProps} />;
                    case 'blurred-card':
                      return <BlurredCardContent {...contentProps} />;
                    default:
                      return <DefaultContent {...contentProps} />;
                  }
                })()
              )}
            </div>
          </Container>
        )}
      </div>
    </SectionWrapper>
  );
};

export default Banner;
export { Banner };
